'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog';
import { ResponseCode } from '#/constants';
import { createOrder_mutationFn } from '#/data/react-query/mutations/create-order';
import {
  EthereumStakingOrderInfo,
  orderList_queryId,
} from '#/data/react-query/queries/order-list';
import { useConfiguredChainId } from '#/data/wagmi/configured';
import { useWriteDirectStakingStakeCompound } from '#/generated';
import { directStakingConfig } from '#/lib/generated';
import { handleTransactionError } from '#/lib/handle-transaction-error';
import { cn } from '#/lib/utils';
import { DialogContentProps, DialogProps } from '@radix-ui/react-dialog';
import { animated, useSpringRef, useTransition } from '@react-spring/web';
import { captureException } from '@sentry/nextjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useActor, useInterpret } from '@xstate/react';
import { t } from 'intl-lib';
import invariant from 'invariant';
import { useEffect, useMemo, useRef } from 'react';
import { usePrevious } from 'react-use';
import { toast } from 'sonner';
import { encodeFunctionData, formatEther, parseEther } from 'viem';
import { useConfig } from 'wagmi';
import { estimateGas } from 'wagmi/actions';
import { Steps } from './components/steps';
import { StakingDialogConfirmation } from './confirmation';
import machine, { MachineContext } from './contexts/machine';
import { StakingDialogInformation } from './information';
import { StakingDialogTransaction } from './transaction';

interface Props {
  originalRow?: EthereumStakingOrderInfo;
}

function StakingImpl({ originalRow }: Props) {
  const chainId = useConfiguredChainId();
  const config = useConfig();
  const queryClient = useQueryClient();
  const createOrder_mutation = useMutation({
    mutationFn: createOrder_mutationFn,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey.includes(orderList_queryId),
      });
    },
  });

  const { writeContractAsync: stakeCompoundAsync } =
    useWriteDirectStakingStakeCompound();
  const interpreter = useInterpret(machine, {
    context: originalRow
      ? {
          withdrawaddr: originalRow.withdrawaddr,
          claimaddr: originalRow.claimaddr,
          count: originalRow.count,
          amount: originalRow.amount,

          paramsSig: originalRow.paramsSig,

          pubkeys: originalRow.pubkeys,
          signatures: originalRow.signatures,
          extradata: BigInt(originalRow.extradata),
          tips: BigInt(originalRow.tips),
        }
      : undefined,
    services: {
      onParamsSig: async (context, event) => {
        console.log('====================================');
        console.log('onParamsSig', context);
        console.log('====================================');
        const { withdrawaddr, claimaddr, count, amount } = context;
        const res = await createOrder_mutation.mutateAsync({
          claimaddr: claimaddr!,
          withdrawaddr: withdrawaddr!,
          count: count!,
          amount: amount!,
        });
        if (res?.data.code === ResponseCode.SUCCESS) {
          const { pubkeys, signatures, paramsSig, extradata, tips } =
            res.data.data!;
          return { pubkeys, signatures, paramsSig, extradata, tips };
        } else {
          toast.error('Error', {
            description: res?.data?.message ?? 'Uncaught error',
          });
          throw new Error(res?.data.message ?? 'Uncaught error');
        }
      },
      onTransaction: async (context, event) => {
        console.log('====================================');
        console.log('onTransaction', context);
        console.log('====================================');
        const {
          withdrawaddr,
          claimaddr,
          count,
          amount,
          pubkeys,
          signatures,
          paramsSig,
          extradata,
          tips,
          overrideGasLimit,
        } = context;

        console.log('====================================');
        console.log('onTransaction', {
          withdrawaddr,
          claimaddr,
          count,
          amount,
          pubkeys,
          signatures,
          paramsSig,
          extradata,
          tips,
          overrideGasLimit,
        });
        console.log('====================================');

        invariant(claimaddr != null, 'claimaddr is required');
        invariant(withdrawaddr != null, 'withdrawaddr is required');
        invariant(pubkeys != null, 'pubkeys is required');
        invariant(signatures != null, 'signatures is required');
        invariant(paramsSig != null, 'paramsSig is required');
        invariant(extradata != null, 'extradata is required');
        invariant(tips != null, 'tips is required');
        invariant(count != null, 'count is required');
        invariant(amount != null, 'amount is required');
        invariant(overrideGasLimit != null, 'overrideGasLimit is required');

        console.log('====================================');
        console.log('stake', {
          withdrawaddr,
          claimaddr,
          count,
          amount,
          pubkeys,
          signatures,
          paramsSig,
          extradata,
          tips,
          overrideGasLimit,
        });
        console.log('====================================');

        try {
          const amountWei = parseEther(String(amount));
          const args = [
            {
              claimAddr: claimaddr,
              withdrawAddr: withdrawaddr,
              pubkeys,
              signatures,
              paramsSig,
              extraData: extradata,
              amount: amountWei,
            },
            BigInt(0),
          ] as const;

          const value = amountWei * BigInt(count);

          const data = encodeFunctionData({
            abi: directStakingConfig.abi,
            functionName: 'stakeCompound',
            args,
          });

          const gasLimit = await estimateGas(config, {
            chainId,
            to: directStakingConfig.address(chainId),
            data,
            value,
          });
          console.log('====================================');
          console.log('value', formatEther(value), 'ETH');
          console.log('estimateGas', gasLimit);
          console.log('====================================');

          const transactionHash = await stakeCompoundAsync({
            args,
            value,
            gas: (gasLimit * parseEther(overrideGasLimit)) / parseEther('1'),
          });

          console.log('====================================');
          console.log('Transaction Success', transactionHash);
          console.log('====================================');

          return { transactionHash };
        } catch (error) {
          handleTransactionError(error);
          captureException(error);
          throw error;
        }
      },
    },
  });
  const [state, dispatch] = useActor(interpreter);

  const stepIndices = useMemo(
    () => ['information', 'confirmation', 'transactionReceipt'],
    []
  );
  const stepIndex = useMemo(
    () =>
      stepIndices.findIndex(parentStateValue =>
        state.matches(parentStateValue)
      ),
    [state, stepIndices]
  );

  const stepComponents = useMemo(
    () => [
      StakingDialogInformation,
      StakingDialogConfirmation,
      StakingDialogTransaction,
    ],
    []
  );
  const stepIndexPrevious = usePrevious(stepIndex);
  const isToLeft = stepIndexPrevious != null && stepIndex > stepIndexPrevious;

  const springRef = useSpringRef();

  const immediateRef = useRef(true);

  const transition = useTransition(stepIndex, {
    ref: springRef,
    keys: null,
    from: {
      opacity: 0,
      transform: isToLeft ? 'translate3d(10%,0,0)' : 'translate3d(-10%,0,0)',
      height: 0,
    },
    enter: {
      opacity: 1,
      transform: 'translate3d(0%,0,0)',
      height: 'auto',
    },
    leave: {
      opacity: 0,
      transform: isToLeft ? 'translate3d(-5%,0,0)' : 'translate3d(5%,0,0)',
      height: 0,
    },
    immediate: immediateRef.current,
  });

  useEffect(() => {
    springRef.start();

    if (immediateRef.current) {
      immediateRef.current = false;
    }
  }, [springRef, stepIndex]);

  return (
    <MachineContext.Provider value={interpreter}>
      <DialogHeader>
        <DialogTitle>
          {/* {translation.StakingAction.Heading} */}
          {t('EthereumStaking.StakingAction.Heading')}
        </DialogTitle>
        <DialogDescription asChild>
          <Steps selectedIndex={stepIndex} className="py-6" />
        </DialogDescription>
      </DialogHeader>
      {transition((style, stepIndex) => {
        const StepComponent = stepComponents[stepIndex];
        return (
          <animated.div style={style} className="flex flex-col gap-4">
            <StepComponent />
          </animated.div>
        );
      })}
    </MachineContext.Provider>
  );
}

export function StakingDialogContent({
  originalRow,
  className,
  ...props
}: Props & DialogContentProps) {
  return (
    <DialogContent
      {...props}
      className={cn(
        'sm:max-w-(--breakpoint-sm) overflow-y-scroll max-h-screen',
        className
      )}
    >
      <StakingImpl originalRow={originalRow} />
    </DialogContent>
  );
}
export function StakingDialog({ originalRow, ...props }: Props & DialogProps) {
  return (
    <Dialog {...props}>
      <StakingDialogContent originalRow={originalRow} />
    </Dialog>
  );
}
